
import React, { useState, useEffect} from 'react';
import Grid from "@mui/material/Grid";
import Box from '@mui/material/Box';
import moment from 'moment/moment';
import { AdminUserSettingsHeaderLink } from '../../components/adminSettingsHeaderLinks/AdminUserSettingsHeaderLink.jsx';
import { requestGetAdditionalPayments, requestUpdateAdditionalPaymentStatus } from "../../services/wisdomServices/additionalPaymentService.js";
import WisdomDataTable from "../../components/dataTable/WisdomDataTable.jsx";
import { Tag } from "primereact/tag";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Can } from '../../security/Can.jsx';
import { PERMISSIONS } from '../../security/permissions.generated.js';

/** 
 * @function AdminAdditionalPaymentsPage 
 * @description - manage additional payments and list, approve, and reject requests for additional payments
 */
function AdminAdditionalPaymentsPage() {
  const [additionalPayments, setAdditionalPayments] = useState([])
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    global: {value:null, matchMode: FilterMatchMode.CONTAINS},
    submited_by: {value:null, matchMode: FilterMatchMode.CONTAINS},
    userName: {value:null, matchMode: FilterMatchMode.CONTAINS}
  })

  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const globalFilters = ['submited_by', 'userName']
  const onGlobalFilterChange = (e) => {
      const value = e.target.value;
      let _filters = { ...filters };

      _filters['global'].value = value;

      setFilters(_filters);
      setGlobalFilterValue(value);
  };


  /**
   * @async
   * @function getAdditionalPayments
   * @description - it searches for payments based on the user's role
   */
  const getAdditionalPayments = async(pageNumber = 1, pageSize = 25) => {
    try {
      setLoading(true)
      let response = await requestGetAdditionalPayments(pageNumber, pageSize)

      const { data, pagination } = response;

      let payments = [];
      for (const item of data) {
        let name = item.userName
        let payment = {
          submited_by: item.manager !=null ? item.manager : " ",
          id: item.id,
          userID : item.userID,
          userName: name,
          userEmail: item.userEmail,
          reason: item.reason,
          amount: item.amount,
          status: item.status,
          createdAt: moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss"),
          approvedBy: item?.approver || "",
          approvalDate: item.approvalDate !=null ? moment(item.approvalDate).format("YYYY-MM-DD HH:mm:ss") : " ",
          comment: item.comment
        }
        payments.push(payment);
      }
      setAdditionalPayments(payments);
      setTotalRecords(pagination.totalResults);
      
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onPageChange = (event) => {
    const newPage = event.page + 1;
    const newRows = event.rows;

    setPage(newPage);
    setRows(newRows);
    setFirst(event.first);

    getAdditionalPayments(newPage, newRows);
  };

  const getSeverity = (status) => {
    switch (status) {
      case 'Approved':
          return 'success';

      case 'Rejected':
          return 'danger';

      case 'Cancelled':
          return 'warning';

      default:
          return null;
    }
  };

  const statusTemplate = (row) => {
    if(row.status === "Pending Approval"){
      return(
        <div className="pending-requests-btns-box">
          <Can permission={PERMISSIONS.additionalPaymentsByIdStatus_patch}>
            <Tag className="pending-requests-btns" onClick={() => changeStatus(row.id, "Approved")} value="Approve" severity={"info"}/>
            <Tag className="pending-requests-btns" onClick={() => changeStatus(row.id, "Rejected")} value="Reject" severity={"danger"}/>
          </Can>
        </div>
      )
    }else{
      return (
        <Tag  value={row.status} severity={getSeverity(row.status)}/>
      )

    }
  }

  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const priceBodyTemplate = (rowData) => {
    return formatCurrency(rowData.amount);
  };

  const renderHeader = () => {
    return (
        <div className="adm-additional-payment-header">
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
            </IconField>
        </div>
    );
  };

  const header = renderHeader()

  const columns = [
    {
      field: 'submited_by',
      header: 'Submited By'
    },
    {
      field: 'userName',
      header: 'Agent Name'
    },
    {
      field: 'reason',
      header: 'Reason'
    },
    {
      header: 'Amount',
      body: priceBodyTemplate
    },
    {
      field: 'comment',
      header: 'Comment'
    },
    {
      header: 'Status',
      body: statusTemplate
    },
    {
      field: 'createdAt',
      header: 'Created At',
    },
    {
      field: 'approvedBy',
      header: 'Approved by'
    },
    {
      field: 'approvalDate',
      header: 'Approval At'
    },
  ]

  let changeStatus= async(id,status) => {
    let keys = {
      status : status
    };
    try {
      await requestUpdateAdditionalPaymentStatus(id, keys);
    } catch (error) {
      console.error(error)
    }
    
    await getAdditionalPayments();
  };

  useEffect(() => {
    getAdditionalPayments(page, 25)
  },[]);
  

  return (
    
    <div className='settingsContainer'>
      <AdminUserSettingsHeaderLink />
      {  
          <Box sx={{ flexGrow: 1}} className="settingsBody">
              <Grid container>
                  <Grid size={10}>
                      <h3 className="settingsLabel">Additional Payments</h3>
                  </Grid>
                  <Grid size={10} className="update-fields">
                      { additionalPayments && (
                        <WisdomDataTable
                          columns={columns}
                          data={additionalPayments}
                          header={header}
                          filters={filters}
                          globalFilters={globalFilters}
                          loading={loading}
                          totalRecords={totalRecords}
                          onPage={onPageChange}
                          first={first}
                          rows={rows}
                        />
                      )}
                  </Grid>
                  
              </Grid>
          </Box>
      }
    </div>
  );
}

export default AdminAdditionalPaymentsPage;
